import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { productsAPI, cartAPI } from '../services/api';
import { availabilityOf } from '../services/stock';
import { productPath } from '../services/productPath';
import { vehicleStore } from '../services/vehicleStore';
import { useT } from '../services/i18n';

// Shown under a cart line that is NOT confirmed for the selected vehicle: best equivalent/similar part that fits, one-click swap
const CartCompatibleSwap = ({ item, vehicle, onReplaced }) => {
  const { t } = useT();
  const [alt, setAlt] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = vehicle ? `${vehicle.brand}|${vehicle.model}|${vehicle.engine?.name || ''}` : '';

  useEffect(() => {
    let cancelled = false;
    setAlt(null);
    if (!vehicle) return undefined;
    productsAPI.compatibleAlternative(item.product_id, vehicleStore.toParams(vehicle)).then(items => {
      if (cancelled) return;
      setAlt((items || []).find(p => availabilityOf(p).status !== 'out') || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [item.product_id, key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!alt || !vehicle) return null;
  const multiple = alt.sale_multiple || 1;
  const qty = Math.max(multiple, Math.ceil((item.quantity * (item.units || 1)) / multiple) * multiple);
  const diff = Math.round((alt.price - item.price) * 100) / 100;
  const v = `${vehicle.brand} ${vehicle.model}`;

  const replace = async () => {
    setBusy(true);
    try {
      await cartAPI.addItem(alt.id, qty);
      await cartAPI.removeItem(item.product_id, item.pack_id || null);
      toast.success(t('alt.replaced', { name: item.name, alt: `${alt.brand} ${alt.name}` }));
      await onReplaced?.();
    } catch (e) {
      toast.error(e.message || t('alt.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid={`cart-compatible-swap-${item.product_id}`}>
      <Link to={productPath(alt)} className="flex items-center gap-2 min-w-0 flex-1 group" data-testid={`cart-compatible-swap-link-${item.product_id}`}>
        <img src={alt.image} alt="" className="w-10 h-10 object-contain bg-white rounded" />
        <div className="min-w-0">
          <p className="text-xs text-green-700 font-medium flex items-center gap-1"><CheckCircle2 size={12} /> {t('alt.compatible', { v, brand: alt.brand })}</p>
          <p className="text-sm text-gray-800 truncate group-hover:underline">{alt.name} · <span className="font-bold">{alt.price.toFixed(2)} €</span> <span className={`text-xs ${diff < 0 ? 'text-green-700' : 'text-gray-500'}`} data-testid={`cart-compatible-swap-diff-${item.product_id}`}>({diff === 0 ? t('alt.samePrice') : diff < 0 ? t('alt.perUnit', { d: Math.abs(diff).toFixed(2) }) : t('alt.morePerUnit', { d: diff.toFixed(2) })})</span></p>
        </div>
      </Link>
      <button type="button" onClick={replace} disabled={busy} data-testid={`cart-compatible-swap-button-${item.product_id}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-green-700 hover:bg-green-800 disabled:opacity-60 rounded-md px-3 py-1.5">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />} {t('alt.replace')}{qty !== item.quantity ? ` (×${qty})` : ''}
      </button>
    </div>
  );
};

export default CartCompatibleSwap;
